import React from 'react';
import { createRoot } from 'react-dom/client';
import { EVENT_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import MobileBlurBanner from 'ts/client/gallery/components/MobileBlurBanner';
import { withTheme } from 'ts/client/gallery/theme';
import ScreenshotDeterrence from 'ts/common/components/gallery/screenshot-deterrence/ScreenshotDeterrence';
import { isMobileDevice } from 'ts/common/components/gallery/screenshot-deterrence/detector';

const mountRoot = (attributes: Record<string, string> = {}) => {
    const el = document.createElement('div');

    Object.entries(attributes).forEach(([name, value]) => el.setAttribute(name, value));
    document.body.appendChild(el);

    return createRoot(el);
};

const initDesktopDeterrence = (
    eventData: SpApi.Client.IEvent,
    translateFilter: SpAngularJs.ITranslateFilter
) => {
    mountRoot().render(
        <ScreenshotDeterrence
            brandName={eventData.name}
            colorScheme={eventData.theme.colorScheme}
            keyboardProtectionEnabled
            logoUrl={eventData.theme.eventBrandLogo?.displayUrl?.original}
            warningBody={
                eventData.screenshotDeterrenceDesktopBody ??
                translateFilter('screenshotWarningBody')
            }
            warningTitle={
                eventData.screenshotDeterrenceDesktopTitle ??
                translateFilter('screenshotWarningTitle')
            }
            windowFocusProtectionLevel="blur"
        />
    );
};

/**
 * The photos blur themselves (see useMobileBlur); this only adds the banner explaining the blur.
 * It lives in a persistent live region so screen readers hear it when it appears.
 */
const initMobileBlurBanner = (
    eventData: SpApi.Client.IEvent,
    spEventBus: SpAngularJs.ISpEventBus,
    translateFilter: SpAngularJs.ITranslateFilter
) => {
    const ThemedMobileBlurBanner = withTheme(MobileBlurBanner, eventData.theme);

    mountRoot({ 'aria-live': 'polite' }).render(
        <ThemedMobileBlurBanner
            blurType={eventData.screenshotDeterrenceMobileType}
            customBody={eventData.screenshotDeterrenceMobileBannerBody}
            customTitle={eventData.screenshotDeterrenceMobileBannerTitle}
            spEventBus={spEventBus}
            translateFilter={translateFilter}
        />
    );
};

export default [
    'spEventBus',
    '$filter',
    function initScreenshotDeterrence(
        spEventBus: SpAngularJs.ISpEventBus,
        $filter: (name: 'translate') => SpAngularJs.ITranslateFilter
    ) {
        const translateFilter = $filter('translate');
        let inited = false;

        spEventBus.on<SpApi.Client.IEvent>(EVENT_EVENTS.GOT, (eventData) => {
            if (inited || !eventData?.theme || !eventData.hasAnyScreenshotDeterrence) {
                return;
            }

            inited = true;

            if (eventData.hasScreenshotDeterrenceDesktop) {
                initDesktopDeterrence(eventData, translateFilter);
            }

            if (
                eventData.screenshotDeterrenceMobileType &&
                eventData.screenshotDeterrenceMobileShowBanner &&
                isMobileDevice()
            ) {
                initMobileBlurBanner(eventData, spEventBus, translateFilter);
            }
        });

        spEventBus.emit(EVENT_EVENTS.GET);
    }
];
